<script setup lang="ts">
import { computed } from 'vue';
import { iSelect, iMenuTriggerAlignOptions } from '@inscribe/signal';
import {
  type InsightDailyBalanceCondition,
  type AmountFilter,
  type TimeframeFilter,
  DailyBalanceConditionOperator,
  AmountFieldFilterOperator,
  TimeframeMode,
} from '@/types/decision-engine/configurable-insights.types';
import AmountFilterEditor from '@/V2/decision-engine/configurable-insights/components/filters/AmountFilterEditor.vue';
import TimeframeFilterEditor from '@/V2/decision-engine/configurable-insights/components/filters/TimeframeFilterEditor.vue';
import FiltersEmptyState from '@/V2/decision-engine/configurable-insights/components/filters/FiltersEmptyState.vue';
import AddFilterButton from '@/V2/decision-engine/configurable-insights/components/filters/AddFilterButton.vue';
import { useConditionEditor } from '@/V2/decision-engine/configurable-insights/composables/useConditionEditor';

interface Props {
  condition: InsightDailyBalanceCondition;
}

interface Emits {
  (e: 'update:condition', value: InsightDailyBalanceCondition): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Filter types available for daily balance
type DailyBalanceFilterType = 'amount' | 'timeframe' | 'volatility';

enum FilterEditorType {
  AMOUNT = 'amount',
  TIMEFRAME = 'timeframe',
}

// Filter configuration
interface FilterConfig {
  key: DailyBalanceFilterType;
  label: string;
  editorType: FilterEditorType;
  defaultValue: AmountFilter | TimeframeFilter;
}

const FILTER_CONFIGS: FilterConfig[] = [
  {
    key: 'amount',
    label: 'Amount',
    editorType: FilterEditorType.AMOUNT,
    defaultValue: {
      operator: AmountFieldFilterOperator.GREATER_THAN,
      values: [0],
    },
  },
  {
    key: 'timeframe',
    label: 'Timeframe',
    editorType: FilterEditorType.TIMEFRAME,
    defaultValue: {
      frequency: null,
      time_period_in_days: null,
      mode: TimeframeMode.SLIDING_WINDOW,
    },
  },
  {
    key: 'volatility',
    label: 'Volatility',
    editorType: FilterEditorType.AMOUNT,
    defaultValue: {
      operator: AmountFieldFilterOperator.GREATER_THAN,
      values: [0],
    },
  },
];

// Operator options
const operatorOptions = [
  { text: 'Minimum', value: DailyBalanceConditionOperator.MINIMUM },
  { text: 'Maximum', value: DailyBalanceConditionOperator.MAXIMUM },
  { text: 'Average', value: DailyBalanceConditionOperator.AVERAGE },
];

// Update handler for operator
const updateOperator = (value: DailyBalanceConditionOperator) => {
  emit('update:condition', {
    ...props.condition,
    operator: value,
  });
};

// Available filter types (those not yet added)
// amount and volatility are mutually exclusive: if one is active, the other appears disabled
const availableFilters = computed(() => {
  const hasAmount = !!props.condition.filters['amount'];
  const hasVolatility = !!props.condition.filters['volatility'];

  return FILTER_CONFIGS.filter(
    (config) => !props.condition.filters[config.key],
  ).map((config) => ({
    text: config.label,
    value: config.key,
    disabled:
      (config.key === 'volatility' && hasAmount) ||
      (config.key === 'amount' && hasVolatility),
  }));
});

type DailyBalanceFilterValue = AmountFilter | TimeframeFilter;

const { activeFilters, addFilter, removeFilter, updateFilter } =
  useConditionEditor<
    InsightDailyBalanceCondition,
    DailyBalanceFilterValue,
    FilterConfig
  >(
    () => props.condition,
    emit,
    () => FILTER_CONFIGS,
  );
</script>

<template>
  <div class="daily-balance-condition-editor d-flex flex-column i-gap-6">
    <div class="header d-flex flex-row align-items-center i-gap-6">
      <div class="condition-text i-text-body-2">Flag if</div>
      <!-- Operator Selection -->
      <i-select
        :model-value="condition.operator"
        :options="operatorOptions"
        placeholder="Select operator"
        @update:model-value="updateOperator"
      />
      <div class="condition-text i-text-body-2">daily balance is</div>
    </div>

    <!-- Filters Section -->
    <div class="filters-section d-flex flex-column i-gap-6">
      <!-- Active Filters -->
      <div
        v-if="activeFilters.length > 0"
        class="active-filters d-flex flex-column i-gap-6 i-p-11 i-radius-5xl"
      >
        <template v-for="filterConfig in activeFilters" :key="filterConfig.key">
          <!-- Amount Filter -->
          <AmountFilterEditor
            v-if="filterConfig.editorType === FilterEditorType.AMOUNT"
            :filter="filterConfig.value as AmountFilter"
            :label="filterConfig.label"
            :is-percentage="filterConfig.key === 'volatility'"
            @update:filter="updateFilter(filterConfig.key, $event)"
            @remove="removeFilter(filterConfig.key)"
          />

          <!-- Timeframe Filter -->
          <TimeframeFilterEditor
            v-else-if="filterConfig.editorType === FilterEditorType.TIMEFRAME"
            :filter="filterConfig.value as TimeframeFilter"
            @update:filter="updateFilter(filterConfig.key, $event)"
            @remove="removeFilter(filterConfig.key)"
          />
        </template>

        <!-- Add Filter Button (shown when filters exist) -->
        <AddFilterButton
          :available-filters="availableFilters"
          @add-filter="(val) => addFilter(val as DailyBalanceFilterType)"
        />
      </div>

      <!-- Empty State with Add Filter button inside -->
      <FiltersEmptyState v-else>
        <template #action>
          <AddFilterButton
            :available-filters="availableFilters"
            :trigger-align="iMenuTriggerAlignOptions.CENTER"
            @add-filter="(val) => addFilter(val as DailyBalanceFilterType)"
          />
        </template>
      </FiltersEmptyState>
    </div>
  </div>
</template>

<style scoped lang="scss">
.daily-balance-condition-editor {
  .filters-section {
    .section-label {
      color: var(--text-neutral-bold);
    }
  }
}
</style>
